import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Pencil, PlusCircle, Trash2, FileText, AlertTriangle, Loader2, Mail, Archive, FileX, Check } from "lucide-react";
import { getContract } from "@/api/contracts";
import { deleteBooking, createBookings, createCustomBookings } from "@/api/bookings";
import { deleteAgreement } from "@/api/agreements";
import { Contract, Booking, ContractStatus, isExplorerMembership } from "@/types";
import { toast } from "sonner";
import { MultiStepAddContractModal } from "@/components/MultiStepAddContractModal";
import { CustomBookingModal } from "@/components/CustomBookingModal";
import { useContracts } from "@/contexts/ContractContext";
import { reduceYears, getAdobeStatusBadgeClass, ucFirst, getStatusColor, formatCurrency } from "@/utils";
import { getReviewActionText } from "@/utils/index";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { BookingsCard } from "@/components/contract-summary/BookingsCard";
import { DocumentsCard } from "@/components/contract-summary/DocumentsCard";
import { MembershipCard } from "@/components/contract-summary/MembershipCard";
import { FinancialCard } from "@/components/contract-summary/FinancialCard";
import { AddressCard } from "@/components/contract-summary/AddressCard";
import { ContactsTabsCard } from "@/components/contract-summary/ContactsTabsCard";
import { EmailCard } from "@/components/contract-summary/EmailCard";
import { NotesCard } from "@/components/contract-summary/NotesCard";
import { FormOfSurrenderCard } from "@/components/contract-summary/FormOfSurrenderCard";
import { ArchiveStatus } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { contractNeedsReview } from "@/utils/contractUtils"; // Import the helper function

export function ContractSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [contract, setContract] = useState<Contract | null>(location.state?.contractData || null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCustomBookingModalOpen, setIsCustomBookingModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteAgreementDialogOpen, setIsDeleteAgreementDialogOpen] = useState(false);
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);
  const [isAddingBooking, setIsAddingBooking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeletingAgreement, setIsDeletingAgreement] = useState(false);
  const [statusChanged, setStatusChanged] = useState(false);
  const { contracts, updateContract, removeBooking, updateContractStatus, deleteContract, fetchContracts } = useContracts();
  const { user } = useAuth(); // Get the current user

  // Check if contract is in a state where editing is not allowed (active or complete)
  const isContractComplete = contract?.status === ContractStatus.Complete;
  const isContractActive = contract?.status === ContractStatus.Active;
  const isEditingDisabled = isContractComplete || isContractActive;

  const fetchContractData = async () => {
    if (!id || id === ContractStatus.New) return;

    try {
      setIsLoading(true);
      const response = await getContract(Number(id));
      if (response && response.success) {
        setContract(response.contract);
      } else {
        toast.error("Failed to fetch contract details");
      }
    } catch (error) {
      toast.error("Error fetching contract details");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractData();
  }, [id]);

  const handleEdit = async (data: Omit<Contract, '_id'>) => {
    if (!contract || !contract._id) {
      toast.error("No contract to update");
      return;
    }

    try {
      setIsUpdating(true);

      // For managers, preserve the existing agreement number to avoid validation errors
      const dataToUpdate = {
        ...data,
        agreementNumber: data.agreementNumber || contract.agreementNumber || 0
      };

      // Call the updateContract function from the context
      await updateContract(contract._id, dataToUpdate);

      toast.success("Contract updated successfully");
      setIsEditModalOpen(false);

      // Fetch the updated contract data from the API
      const response = await getContract(contract._id);
      if (response && response.success) {
        setContract(response.contract); // Update the local state with fresh data
      } else {
        toast.error("Failed to fetch updated contract details");
      }
    } catch (error) {
      toast.error("Failed to update contract");
      console.error("Error updating contract:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCustomBookingSubmit = async (booking: Omit<Booking, '_id'>) => {
    if (isAddingBooking || !contract) return;

    try {
      setIsAddingBooking(true);

      // Create an array of bookings to be sent to the API
      const bookingsToAdd = [{
        ...booking,
        contractId: contract._id
      }];

      // Call API to create bookings
      const response = await createCustomBookings(contract._id, bookingsToAdd);

      // Refresh the contract data after successfully adding the booking
      const updatedContractResponse = await getContract(contract._id);
      if (updatedContractResponse && updatedContractResponse.success) {
        setContract(updatedContractResponse.contract); // Update the local state with the refreshed contract
      }

      // Update contract status to pending
      updateContractStatus(contract._id, ContractStatus.Pending);

      toast.success("Custom booking added successfully");

      // Explicitly close the modal
      setIsCustomBookingModalOpen(false);
    } catch (error) {
      toast.error("Failed to add custom booking");
    } finally {
      setIsAddingBooking(false);
    }
  };

  const handleDeleteBooking = async (bookingId: number) => {
    if (isDeletingBooking) {
      console.warn(`Delete already in progress for booking ID: ${bookingId}`);
      return; // Prevent multiple calls
    }

    console.log(`Attempting to delete booking ID: ${bookingId}`); // Debug log

    // Verify if the booking exists in the local state
    const bookingExists = contract?.bookings?.some(b => b._id === bookingId);
    if (!bookingExists) {
      toast.error("Booking not found in the current contract.");
      return;
    }

    try {
      if (!contract || !contract._id) return;

      setIsDeletingBooking(true);

      const response = await deleteBooking(bookingId);

      if (response.success) {
        console.log(`Booking ID ${bookingId} deleted successfully`);

        // Remove the booking from the local state
        setContract(prev => {
          if (!prev) return null;
          return {
            ...prev,
            bookings: prev.bookings?.filter(b => b._id !== bookingId) || []
          };
        });

        toast.success(response.message || "Booking removed successfully");
      } else {
        toast.error(response.message || "Failed to remove booking");
      }
    } catch (error) {
      console.error(`Error deleting booking ID ${bookingId}:`, error);
      toast.error("Failed to remove booking");
    } finally {
      setIsDeletingBooking(false);
    }
  };

  const handleDeleteAgreement = async () => {
    if (!contract || !contract._id) {
      toast.error("No contract to cancel agreement");
      return;
    }

    try {
      setIsDeletingAgreement(true);

      const response = await deleteAgreement(contract._id);

      if (response && response.success) {
        // Update the contract status to indicate the agreement has been deleted
        setContract(prev => {
          if (!prev) return null;
          return {
            ...prev,
            adobeStatus: null, // Clear the adobe status
            status: ContractStatus.Pending // Set status back to pending
          };
        });

        // Update contract in context
        updateContractStatus(contract._id, ContractStatus.Pending);

        // Refresh all contracts from the backend
        await fetchContracts();

        // Fetch updated contract data from the API to ensure UI is in sync
        const updatedContractResponse = await getContract(contract._id);
        if (updatedContractResponse && updatedContractResponse.success) {
          setContract(updatedContractResponse.contract);
        }

        toast.success(response.message || "Agreement canceled successfully");
        setIsDeleteAgreementDialogOpen(false);
      } else {
        toast.error(response?.message || "Failed to Cancel Agreement");
      }
    } catch (error: any) {
      console.error("Error cancel agreement:", error);
      toast.error(error.message || "Failed to cancel agreement");
    } finally {
      setIsDeletingAgreement(false);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    if (!contract) return;

    // Update the local state
    setContract(prev => {
      if (!prev) return null;
      return {
        ...prev,
        status: newStatus
      };
    });

    // Mark that status has been changed
    setStatusChanged(true);
  };

  const handleSave = async () => {
    try {
      if (!contract || !contract._id) {
        toast.error("No contract to save");
        return;
      }
      
      // Agreement Number is required for admin or super_admin only (except for Explorer memberships)
      if ((user?.role === 'admin' || user?.role === 'super_admin') && (!contract.agreementNumber || contract.agreementNumber === 0) && !isExplorerMembership(contract.owner.membershipLevel)) {
        toast.error("Agreement Number is required for contracts that need review");
        return;
      }

      // Send the full contract object to the API
      await updateContract(contract._id, contract);

      setStatusChanged(false); // Reset statusChanged flag
      navigate("/contracts");
      toast.success("Contract saved successfully");
    } catch (error) {
      console.error("Error saving contract:", error);
      toast.error("Failed to save contract");
    }
  };

  const handleDeleteContract = async () => {
    try {
      if (!contract || !contract._id) {
        toast.error("No contract to delete");
        return;
      }

      await deleteContract(contract._id);
      navigate("/contracts");
    } catch (error) {
      toast.error("Failed to delete contract");
    }
  };

  const handleArchiveContract = async () => {
    if (!contract || !contract._id) {
      toast.error("No contract to archive");
      return;
    }

    try {
      setIsArchiving(true);

      // Update the contract with archiveStatus set to "complete" instead of "pending"
      const updatedContract = {
        ...contract,
        archiveStatus: ArchiveStatus.Complete // Changed from "pending" to "complete"
      };

      // Call the updateContract function to update the contract
      await updateContract(contract._id, updatedContract);

      toast.success("Contract archived successfully");
      navigate("/contracts"); // Redirect to the contracts page
    } catch (error) {
      console.error("Error archiving contract:", error);
      toast.error("Failed to archive contract");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    fetchContractData(); // Fetch updated contract data when the modal is closed
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg">Loading contract details...</span>
      </div>
    );
  }

  if (!contract) {
    return <div>Contract not found</div>;
  }

  // Get the button text based on the contract and user role
  const saveButtonText = getReviewActionText(contract, user?.role || "");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Contract Summary</h1>
        <div className="flex space-x-4">
          {isContractComplete ? (
            <Button
              variant="outline"
              onClick={handleArchiveContract}
              className="gap-2"
              disabled={isArchiving}
            >
              {isArchiving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
              Archive
            </Button>
          ) : (
            <>
              {isContractActive && (
                <AlertDialog open={isDeleteAgreementDialogOpen} onOpenChange={setIsDeleteAgreementDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2 bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100">
                      <FileX className="h-4 w-4" />
                      Cancel
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Agreement</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this agreement? This action will cancel the agreement process.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAgreement}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        disabled={isDeletingAgreement}
                      >
                        {isDeletingAgreement ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          'Cancel Agreement'
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(true)}
                className="gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>

              <Button
                variant="outline"
                className={`gap-2 ${contract.status === ContractStatus.Pending && contract.owner.emailVerifiedAt ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' : ''}`}
                disabled={contract.status !== ContractStatus.Pending && contract.status !== ContractStatus.Active && contract.status !== ContractStatus.Complete}
                onClick={async () => {
                  try {
                    const { sendOfferConfirmationEmail } = await import('@/api/offerConfirmation');
                    const response = await sendOfferConfirmationEmail(contract._id);
                    
                    if (response.success) {
                      toast.success(response.message || "Email sent successfully");
                      // Refresh contract data to get updated emailVerifiedAt
                      await fetchContractData();
                    } else {
                      toast.error(response.message || "Failed to send email");
                    }
                  } catch (error: any) {
                    toast.error(error.message || "Failed to send email");
                  }
                }}
              >
                <Mail className="h-4 w-4" />
                Send Confirmation Email
                {contract.owner.emailVerifiedAt && <Check className="ml-2 h-4 w-4 text-green-500" />}
              </Button>

              <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="gap-2">
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the contract and all associated bookings.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteContract}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Button onClick={handleSave}>{saveButtonText}</Button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="md:col-span-1">
          <MembershipCard
            membershipNumber={contract.owner.membershipNumber}
            agreementNumber={contract.agreementNumber}
            membershipLevel={contract.owner.membershipLevel}
            termYears={contract.termYears}
            pointsRightsPurchased={contract.pointsRightsPurchased}
            newPointsPurchased={contract.newPointsPurchased}
            firstYearUse={contract.firstYearUse}
            pointsRightsDueDate={contract.pointsRightsDueDate}
            status={contract.status}
            onStatusChange={handleStatusChange}
            isStatusEditingDisabled={isEditingDisabled}
            userRole={user?.role}
          />
        </div>

        <ContactsTabsCard
          owner={contract.owner}
          additionalContacts={contract.additionalContacts}
          userHistory={contract.userHistory}
          emailVerifiedAt={contract.owner.emailVerifiedAt}
        />

        <FinancialCard
          purchasePrice={contract.purchasePrice}
          pointPrice={contract.pointPrice}
          paymentTerms={contract.paymentTerms}
          contractDate={contract.contractDate}
        />

        <AddressCard
          street={contract.owner.address?.street || ""}
          buildingNumber={contract.owner.address?.buildingNumber || ""}
          town={contract.owner.address?.town || ""}
          stateProvince={contract.owner.address?.stateProvince || ""}
          postCode={contract.owner.address?.postCode || ""}
          country={contract.owner.address?.country || ""}
        />

        <Card>
          <CardHeader>
            <CardTitle>Payment Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            {contract.paymentInstallments && contract.paymentInstallments.length > 0 ? (
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payment Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Paid Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contract.paymentInstallments.map((payment, index) => (
                      <TableRow key={`payment-${index}`}>
                        <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                        <TableCell>{formatCurrency(payment.amount, 'en-GB', 'GBP')}</TableCell>
                        <TableCell className="text-muted-foreground">Not Tracked</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                No payment installments found
              </div>
            )}
          </CardContent>
        </Card>

        <FormOfSurrenderCard
          formOfSurrenders={contract.formOfSurrenders || []}
          notes={contract.notes?.formOfSurrenderNotes}
        />

        <NotesCard 
          paymentNotes={contract.notes?.paymentNotes}
          reasonForPurchase={contract.notes?.internalNotes}
          welcomeEmailNotes={contract.notes?.welcomeEmailNotes}
        />

        <BookingsCard
          contract={contract}
          bookings={contract.bookings || []}
          onDeleteBooking={handleDeleteBooking}
          onAddCustomBooking={handleCustomBookingSubmit}
          disabled={isEditingDisabled} // Pass the combined disabled state
        />

        <DocumentsCard
          contract={contract}
          documents={contract.documents || []}
          onDocumentsChange={fetchContractData}
          disabled={isEditingDisabled}
        />
      </div>

      <MultiStepAddContractModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose} // Use the updated onClose handler
        isLoading={isUpdating}
        contractData={contract}
        onSubmit={handleEdit}
        isEditMode={!isEditingDisabled} // Disable editing if contract is active or complete
      />

      <CustomBookingModal
        isOpen={isCustomBookingModalOpen}
        onClose={() => setIsCustomBookingModalOpen(false)}
        onSubmit={handleCustomBookingSubmit}
        contractId={contract._id}
      />
    </div>
  );
}